import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText,
  Box, Typography, List, ListItem, ListItemText, Paper, Alert, CircularProgress
} from '@mui/material';

const AiDialog = ({
  open,
  onClose,
  title,
  content,
  dialogType,
  refreshTree,
  loading
}) => {
  // Function to render consistency check response in a structured way
  const renderConsistencyContent = (data) => {
    // Check if we have an error message or empty data
    const isError = !data ||
      (typeof data === 'object' && Object.keys(data).length === 0) ||
      (data.consistency && data.consistency.toLowerCase().includes('error')) ||
      (data.summary && data.summary.toLowerCase().includes('unavailable'));

    if (isError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {data?.consistency || data?.summary || 'Error connecting to AI service. Please try again later.'}
        </Alert>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Consistency Analysis */}
        <Paper sx={{ p: 2 }} variant="outlined">
          <Typography variant="h6" gutterBottom>Consistency Analysis</Typography>
          <Typography variant="body1">
            {data?.consistency || data?.summary || 'No consistency analysis available.'}
          </Typography>
        </Paper>

        {/* Issues */}
        <Paper sx={{ p: 2 }} variant="outlined">
          <Typography variant="h6" gutterBottom>Issues</Typography>
          {data?.issues?.length > 0 ? (
            <List dense>
              {data.issues.map((issue) => (
                <ListItem key={issue} sx={{ py: 0.5 }}>
                  <ListItemText primary={issue} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1">No issues detected.</Typography>
          )}
        </Paper>

        {/* Calculations if available */}
        {data?.calculations && (
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="h6" gutterBottom>Calculations</Typography>
            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
              {data.calculations}
            </Typography>
          </Paper>
        )}

        {/* Recommendations */}
        <Paper sx={{ p: 2 }} variant="outlined">
          <Typography variant="h6" gutterBottom>Recommendations</Typography>
          {data?.recommendations?.length > 0 ? (
            <List dense>
              {data.recommendations.map((rec) => (
                <ListItem key={rec} sx={{ py: 0.5 }}>
                  <ListItemText primary={rec} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1">No recommendations available.</Typography>
          )}
        </Paper>
      </Box>
    );
  };

  const renderContent = () => {
    // If loading is true, show loading state
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // If content is null, show loading state as fallback
    if (content === null) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // If there's no content or it's empty string
    if (!content || (typeof content === 'string' && !content.trim())) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>
            No data returned from AI service. Please try again later.
          </Typography>
        </Box>
      );
    }

    // Check if content is a React element (from JSX)
    if (React.isValidElement(content)) {
      return content;
    }

    // For consistency check dialog with raw data
    if (dialogType === 'consistency' && typeof content === 'object' && !React.isValidElement(content)) {
      return renderConsistencyContent(content);
    }

    // For string content
    if (typeof content === 'string') {
      return (
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {content}
        </DialogContentText>
      );
    }

    // For any other object content, just return as is
    return content;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {dialogType === 'decompose' && (
          <Button
            onClick={() => {
              onClose();
              refreshTree();
            }}
            color="primary"
            variant="contained"
          >
            View Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
  dialogType: PropTypes.string,
  refreshTree: PropTypes.func,
  loading: PropTypes.bool
};

export default AiDialog;
